import React, { useContext, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Badge, Form, Spinner, Table } from "react-bootstrap";
import moment from "moment";
import SidebarContext from "../../components/Sidebar/SidebarContext";
import StarekContext from "../../context/StarekContext";

const GET_INTEGRATION_EXECUTIONS = gql`
    query GetIntegrationExecutions($where: IntegrationExecutionWhereInput!, $orderBy: [IntegrationExecutionOrderByWithRelationInput!]) {
        integrationExecutions(where: $where, orderBy: $orderBy, take: 100) {
            id
            action
            status_code
            response_body
            error_message
            is_test
            executed_at
            duration_ms
        }
    }
`;

function statusVariant(code) {
    if (!code) return "secondary";
    if (code >= 200 && code < 300) return "success";
    if (code >= 400) return "danger";
    return "warning";
}

const IntegrationExecutionHistory = () => {
    const { entity } = useContext(SidebarContext);
    const starekContext = useContext(StarekContext);
    const integrationId = entity?.id;

    const [startDate, setStartDate] = useState(
        moment().subtract(6, "days").startOf("day").format("YYYY-MM-DD"),
    );
    const [endDate, setEndDate] = useState(
        moment().endOf("day").format("YYYY-MM-DD"),
    );
    const [expanded, setExpanded] = useState(null);

    const { data, loading } = useQuery(GET_INTEGRATION_EXECUTIONS, {
        client: starekContext.engineClient,
        variables: {
            where: { integration_id: { equals: integrationId } },
            orderBy: [{ executed_at: "desc" }],
        },
        skip: !integrationId,
        fetchPolicy: "network-only",
    });

    const executions = data?.integrationExecutions ?? [];

    const filtered = executions.filter((ex) => {
        const d = moment(ex.executed_at);
        return (
            d.isSameOrAfter(moment(startDate).startOf("day")) &&
            d.isSameOrBefore(moment(endDate).endOf("day"))
        );
    });

    if (!integrationId) {
        return (
            <div className="p-3 text-muted text-center small">
                Save the integration first to see execution history.
            </div>
        );
    }

    return (
        <div className="p-3">
            <div className="d-flex gap-2 align-items-end mb-3">
                <Form.Group className="flex-fill">
                    <Form.Label className="small mb-1">From</Form.Label>
                    <Form.Control
                        type="date"
                        size="sm"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </Form.Group>
                <Form.Group className="flex-fill">
                    <Form.Label className="small mb-1">To</Form.Label>
                    <Form.Control
                        type="date"
                        size="sm"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </Form.Group>
            </div>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-muted text-center py-4 small">
                    No executions found in this date range.
                </div>
            ) : (
                <Table size="sm" hover responsive>
                    <thead>
                        <tr>
                            <th>Date / Time</th>
                            <th>Action</th>
                            <th className="text-center">Status</th>
                            <th className="text-end">Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((ex) => (
                            <React.Fragment key={ex.id}>
                                <tr
                                    className="cursor-pointer"
                                    onClick={() =>
                                        setExpanded(expanded === ex.id ? null : ex.id)
                                    }
                                >
                                    <td>
                                        {moment
                                            .utc(ex.executed_at)
                                            .local()
                                            .format("MMM D, HH:mm:ss")}
                                        {ex.is_test && (
                                            <Badge
                                                bg="info"
                                                className="ms-2 font-size-10px"
                                            >
                                                test
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="text-muted small">
                                        {ex.action || "—"}
                                    </td>
                                    <td className="text-center">
                                        {ex.status_code ? (
                                            <Badge bg={statusVariant(ex.status_code)}>
                                                {ex.status_code}
                                            </Badge>
                                        ) : (
                                            <Badge bg="secondary">—</Badge>
                                        )}
                                    </td>
                                    <td className="text-end text-muted small">
                                        {ex.duration_ms != null
                                            ? `${ex.duration_ms} ms`
                                            : "—"}
                                    </td>
                                </tr>
                                {expanded === ex.id && (
                                    <tr>
                                        <td colSpan={4} className="bg-light p-2">
                                            {ex.error_message && (
                                                <div className="alert alert-danger py-1 px-2 mb-2 small">
                                                    {ex.error_message}
                                                </div>
                                            )}
                                            <pre
                                                className="mb-0 p-2 border rounded bg-white log-preview max-height-180px font-size-12px"
                                            >
                                                {ex.response_body ?? "(no response body)"}
                                            </pre>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
};

export default IntegrationExecutionHistory;
